/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ProductDisplay from "./ProductDisplay";
import Footer from "./Footer";
import SidePanel from "./sidePanel/SidePanel";
import Print from "../../../../printConfigration/PrintConfigration";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import { useUser } from "../../components/UserContext";
import useCategory from "../../hooks/useCategory";
import { getOrder, postOrder, getLedgersCustomer } from "../../../../shared/services/services";
import { CustomerData, Order, ProductDetail } from "../../../../shared/services/interface";
import useUOM from "src/shared/hooks/useUOM";
import CtrlFOpenPopup from "./searchModel/SearchProduct";
import NotesProducts from "./NotesProducts";
import { errorHandling } from "src/shared/utils/errorHandling";
import SubscriptionDialog from "src/shared/modules/admin/auth/components/SubscriptionDialog";
import useProductsIndexDB from "../../hooks/useProductIndexDB";

type OrderedItem = { productId: string; quantity: number };

const GroceryHome: React.FC = () => {
  const { user } = useUser();
  const { product, loadingPro, productSync } = useProductsIndexDB();
  const { category, loading } = useCategory();
  const { uom } = useUOM();
  const [orderProduct, setOrderProduct] = useState<OrderedItem[]>([]);
  const [selectProductList, setSelectProductList] = useState<ProductDetail[]>([]);
  const [lastProduct, setLastSelectedProduct] = useState<string>("");
  const [viewProduct, setViewProduct] = useState<boolean>(true);
  const [discount, setDiscount] = useState({ discountedAmount: "", discountValue: "", mode: "" });
  const [customerDetail, setCustomerDetail] = useState<CustomerData>();
  const [receivedAmount, setReceivedAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [clearData, setClearData] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastOrderData, setLastOrderData] = useState<Order>();
  const [osb, setOSB] = useState(0);
  const [openPrint, setOpenPrint] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string>("Delivered");
  const [resvedAmount, setResvedAmount] = useState<number>(0);
  const { subscriptionInfo } = useUser();
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState<React.ReactNode>("");

  /** 📦 Add Product to Order */
  const sendToOrder = useCallback((productId: string) => {
    setOrderProduct((prev) => {
      const existing = prev.find((p) => p.productId === productId);

      if (productId === "other") {
        if (existing) {
          return prev.map((p) => (p.productId === "other" ? { ...p, quantity: p.quantity + 1 } : p));
        }

        return [
          ...prev,
          {
            productId: "other",
            productName: "Other",
            arabicword: "آحرون",
            price: 0,
            quantity: 1,
            uom: "",
            notes: "",
          },
        ];
      }

      return existing ? prev.map((p) => (p.productId === productId ? { ...p, quantity: p.quantity + 1 } : p)) : [...prev, { productId, quantity: 1 }];
    });

    setLastSelectedProduct(productId);
  }, []);
  /** 💰 Confirm Order Discount */
  const orderConfirm = useCallback((discountedAmount: number, discountValue: number, mode: string) => {
    setDiscount({
      discountedAmount: String(discountedAmount),
      discountValue: String(discountValue),
      mode,
    });
  }, []);

  /** 🔄 Reset order when clear */
  useEffect(() => {
    setOrderProduct([]);
    setLastSelectedProduct("");
  }, [clearData]);

  useEffect(() => {
    if (!subscriptionInfo) return;

    const alreadyShown = sessionStorage.getItem("subscription_dialog_shown");
    if (alreadyShown === "true") return;

    // GRACE PERIOD CASE
    if (subscriptionInfo.status === "grace_period") {
      const days = subscriptionInfo.daysRemaining;

      const formattedMessage = (
        <>
          This is a reminder that your current subscription will expire in <strong>{days} day(s)</strong>. Please renew now to avoid service disruption.
        </>
      );

      setMessage(formattedMessage);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }

    // WARNING CASE
    if (subscriptionInfo.warning) {
      setMessage(subscriptionInfo.warning);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }
  }, [subscriptionInfo]);

  /** 🧮 Calculate totals */
  const totalPrice = selectProductList.reduce((sum, item) => {
    const qty = Number(item.qty) || 0;
    const price = Number(item.price) || 0;

    const notesTotal = item.notes?.reduce((acc, note) => acc + (Number(note.amount) || 0), 0) || 0;

    return sum + qty * price + notesTotal;
  }, 0);
  const finalAmount = useMemo(() => totalPrice - Number(discount.discountedAmount || 0), [totalPrice, discount]);

  const isUnknownCustomer = customerDetail?.Name === "XXXX";

  const totalPayable = isUnknownCustomer ? finalAmount : finalAmount + Number(customerDetail?.Out_standing_balance || 0);

  /** ✅ Product payload */
  const Products = useMemo(
    () =>
      selectProductList
        .filter((i) => i.productId && i.productName && i.qty && i.price)
        .map((i) => ({
          productName: i.productName,
          arabicword: i.productName === "Other" ? "حرون" : i.arabicword || "",
          amount: Number(i.price),
          quantity: Number(i.qty),
          totalPrice: Number(i.qty) * Number(i.price),
          productId: i.productId,
          uom: i.uom,
          addOns: i.notes,
          itsCustomProduct: i.productName === "Other" ? "true" : "false",
        })),

    [selectProductList],
  );

  const remainingPrice = () => {
    const remaining = Math.max(0, finalAmount - receivedAmount);
    return remaining.toFixed(2);
  };

  const roundToTwo = (value: number) => {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  };

  const paid = Number(receivedAmount || 0);
  const payable = Number(totalPayable || 0);

  // Calculate applied amount and return amount based on payment vs payable
  let appliedAmount = 0;
  let changeAmount = 0;

  if (paid >= payable) {
    // Full payment or overpayment
    appliedAmount = payable; // Only apply what's due
    changeAmount = roundToTwo(paid - payable); // Return the excess as change
  } else {
    // Partial payment
    appliedAmount = paid;
    changeAmount = 0; // No change for partial payment
  }

  /** 🧾 Confirm Order */
  const confirm = useCallback(() => {
    setIsLoading(true);
    const now = new Date();
    const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
    const payamount = finalAmount < receivedAmount ? finalAmount : receivedAmount

    const Orderdate = localDate;
    const Ordertime = now.toTimeString().split(" ")[0];
    const data = {
      Customerid: customerDetail?._id.$oid,
      Products: Products.map((product) => ({
        ...product,
        uom: uom.find((i) => i.id === product.uom)?.uom_name || "",
      })),
      OrderStatus: orderStatus === "Order Taken" ? "Confirm" : orderStatus,
      Orderdate,
      Ordertime,
      totalAmount: totalPrice,
      discount: Number(discount.discountedAmount),
      Finalamount: finalAmount,
      outstanding_balance: Number(customerDetail?.Out_standing_balance) ?? 0,
      balanceReturned: changeAmount,
      payments: [{ Paymentmethod: paymentMethod, ReceivedAmount: appliedAmount }],
      paymentHistory: [
        {
          Paymentmethod: paymentMethod,
          ReceivedAmount: payamount,
          balance: Number(remainingPrice()),
          paidAmount: receivedAmount,
          balanceReturned: changeAmount,
          date: Orderdate,
        },
      ],
      RemainingAmount: Number(remainingPrice()),
    };
    postOrder(data)
      .then((res) => {
        setClearData(res.data.Id);
        setReceivedAmount(0);
        getLedgersCustomer(customerDetail?._id.$oid ?? "")
          .then((res) => setOSB(res.data.ledger.total_outstanding_balance))
          .catch(console.error);
        getOrder(res.data.Id)
          .then((res) => {
            setLastOrderData(res.data.result);
            setOpenPrint(true);
          })
          .catch(console.error);

      })
      .catch((error) => {
        errorHandling(error);
      })
      .finally(() => setIsLoading(false));
  }, [Products, customerDetail, discount, paymentMethod, receivedAmount, totalPrice, finalAmount, orderStatus]);

  /** 🖨️ Print Data */
  const PrintData = useMemo(() => {
    const paidAmount = lastOrderData?.payments?.reduce((sum, p) => sum + Number(p.ReceivedAmount), 0) || 0;
    const totalAmount =
      lastOrderData?.Products?.reduce((sum, p) => {
        const notesTotal = p.addOns?.reduce((acc, note) => acc + (Number(note.amount) || 0), 0) || 0;
        return sum + Number(p.amount) * Number(p.quantity) + notesTotal;
      }, 0) || 0;
    const balance = Number(lastOrderData?.Finalamount || 0) - paidAmount;

    // Calculate return amount and balance/due
    const paid = Number(receivedAmount || 0);
    const payable = Number(totalPayable || 0);
    let dueAmount = 0;
    if (paid >= payable) {
      dueAmount = 0; // Nothing due
    } else {
      dueAmount = payable - paid; // Amount still owed
    }

    return {
      CustomerName: lastOrderData?.CustomerName || "",
      CustomerPhon: lastOrderData?.CustomerPhoneNo || "",
      OrderId: lastOrderData?.Orderid || "",
      OrderStatus: lastOrderData?.OrderStatus || "",
      discount: Number(lastOrderData?.discount || 0),
      Date: lastOrderData?.Orderdate || "",
      Time: lastOrderData?.Ordertime || "",
      Products: lastOrderData?.Products || [],
      Paid: paidAmount,
      unpaid: balance > 0 ? balance : 0,
      returnAmount: Number(lastOrderData?.balanceReturned || 0), // Change returned to customer
      balance: dueAmount, // Amount still due (for partial payments)
      OSB: osb,
      total: totalAmount,
      subtotal: totalAmount - Number(lastOrderData?.discount || 0),
      clientId: lastOrderData?.client_id,
      businessType: user?.business_type,
      paymentMethod: paymentMethod,
      billNo: Number(lastOrderData?.billNumber || 0),
      outstanding_balance: osb,
      payMentHistory: lastOrderData?.paymentHistory || [],
      sellerName: lastOrderData?.createdBy || "",
      balanceEnabiled: true,
      resvedAmount: Number(lastOrderData?.paymentHistory?.[0]?.paidAmount) || 0,
      balanceReturned:
        lastOrderData?.paymentHistory?.[0]?.balanceReturned || 0,
    };
  }, [lastOrderData, osb, user, paymentMethod, resvedAmount, receivedAmount, totalPayable]);

  return (
    <div className="flex flex-col w-full h-full p-1 gap-0.5">
      {(loading || loadingPro || isLoading) && <LoadingScreen />}
      {/* Main Content Area */}
      <div className="flex flex-col h-[90%] lg:flex-row gap-2 flex-grow overflow-auto rounded-xl">
        {/* Product Display (Left on Desktop, Full width on Mobile) */}
        {viewProduct && (
          <div className="w-full lg:w-3/5 bg-white rounded-xl shadow-md p-2 h-full">
            <ProductDisplay
              product={product}
              category={category}
              sendToOrder={sendToOrder}
              clearData={clearData}
              productLoading={setIsLoading}
              syncProduct={productSync}
            />
          </div>
        )}

        {/* Order Section */}
        <div className={`${viewProduct ? "lg:w-[60%]" : "lg:w-2/3"} w-full bg-white rounded-xl shadow-md p-2`}>
          <NotesProducts
            product={product}
            orderProduct={orderProduct}
            lastProduct={lastProduct}
            updateProduct={setSelectProductList}
            viewProduct={() => setViewProduct(!viewProduct)}
            closeProduct={!viewProduct}
            clearData={clearData}
            getUOM={uom}
          />
        </div>
        {/* {toBoolean(userConfigration?.isProductsNotesEnabled) ? (
        ) : (
          <div className={`${viewProduct ? "lg:w-2/5" : "lg:w-2/3"} w-full bg-white rounded-xl shadow-md p-2`}>
            <ProductOrder
              product={product}
              orderProduct={orderProduct}
              lastProduct={lastProduct}
              updateProduct={setSelectProductList}
              viewProduct={() => setViewProduct(!viewProduct)}
              closeProduct={!viewProduct}
              clearData={clearData}
              getUOM={uom}
            />
          </div>
        )} */}

        {/* Side Panel (Right Section) */}
        {!viewProduct && (
          <div className="w-full lg:w-1/3 bg-white rounded-xl shadow-md p-2">
            <SidePanel
              finalAmount={finalAmount}
              selectProductList={selectProductList}
              discount={discount}
              upDateCustomer={setCustomerDetail}
              receivedAmount={(data, method) => {
                setReceivedAmount(data);
                setPaymentMethod(method);
              }}
              clearData={clearData}
              throwStatus={(status: string) => setOrderStatus(status)}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="w-full bg-white rounded-xl shadow-md p-2 h-fit">
        <Footer
          selectProductList={selectProductList}
          orderConfirm={orderConfirm}
          receivedAmount={customerDetail}
          closeProduct={viewProduct}
          placeConfirm={confirm}
          clearData={clearData}
          closeProductTab={() => setViewProduct(false)}
          paymentMethod={paymentMethod}
          received={receivedAmount}
          setResvedAmount={setResvedAmount}
          resvedAmount={resvedAmount}
          isUnknownCustomer={customerDetail?.Name === "XXXX"}
          hasCustomer={!!customerDetail}
        />
      </div>
      <SubscriptionDialog
        open={showDialog}
        message={message}
        onClose={() => setShowDialog(false)}
        onRenew={() => {
          setShowDialog(false);
        }}
      />
      <CtrlFOpenPopup />
      {/* Print */}
      {openPrint && <Print PrintData={PrintData} username="" openPrint={lastOrderData?._id.$oid} isClosed={() => setOpenPrint(false)} barcodeTrue />}
    </div>
  );
};

export default React.memo(GroceryHome);
